import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { DataTable, DataTableColumn, DateField, EmptyState, LoadingButton, Notice, PageHeader } from '@routes-plan/ui';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { useT } from '../../../i18n';
import DateRangeOfficeFilter, { RangeFilter, filterParams } from '../shared/DateRangeOfficeFilter';
import { INVENTORY_ROUTES, OfficeOption, ShowAlerts, apiErrorMessage, fetchOffices, formatDate, formatMoney, newRequestId } from '../shared/inventoryApi';
import { FieldGrid, FilterGrid, FormFooter, IntField, PageBody, PageTabs, SaveButton, Section, fullRow, useInventoryAlert } from '../shared/ui';

type Translate = ReturnType<typeof useT>;
// Opciones con texto traducido; los `value` son los que espera el API.
const REASONS = (t: Translate) => [
  { value: 'DEFECTO_FABRICA', label: t('writeOffs.returns.reason.DEFECTO_FABRICA') },
  { value: 'GARANTIA', label: t('writeOffs.returns.reason.GARANTIA') },
  { value: 'OTRO', label: t('writeOffs.returns.reason.OTRO') },
];
const CONDITIONS = (t: Translate) => [
  { value: 'DANADO', label: t('writeOffs.returns.condition.DANADO') },
  { value: 'BUEN_ESTADO', label: t('writeOffs.returns.condition.BUEN_ESTADO') },
];
const RESOLUTIONS = (t: Translate) => [
  { value: 'CAMBIO', label: t('writeOffs.returns.resolution.CAMBIO') },
  { value: 'DESCUENTO', label: t('writeOffs.returns.resolution.DESCUENTO') },
];
const labelOf = (list: { value: string; label: string }[], value: string) => list.find((x) => x.value === value)?.label ?? value;

/** Devoluciones de producto por defecto de fábrica o garantía. */
const ProductReturns: React.FC<ShowAlerts> = () => {
  const t = useT();
  const { canCreate, canRead, isLoadingPermissions } = useResourcePermissions(INVENTORY_ROUTES.returns);
  const tabs = [
    { key: 'registrar', label: t('writeOffs.returns.tabRegister'), allowed: canCreate },
    { key: 'historial', label: t('writeOffs.tabHistory'), allowed: canRead },
  ].filter((tb) => tb.allowed);
  const [tab, setTab] = useState(0);
  setPageTitle('writeOffs.returns.pageTitle');
  const index = Math.min(tab, Math.max(0, tabs.length - 1));
  const current = tabs[index];

  return (
    <PageBody>
      <PageHeader title={t('writeOffs.returns.title')} description={t('writeOffs.returns.description')} helpAction={<ScreenTourButton />} />
      {tabs.length === 0 ? (
        isLoadingPermissions ? null : <EmptyState icon={<LockOutlinedIcon fontSize="inherit" />} title={t('writeOffs.noSectionPermission')} />
      ) : (
        <>
          <PageTabs value={index} onChange={setTab} labels={tabs.map((tb) => tb.label)} tour="returns-tabs" />
          {current?.key === 'registrar' && <ReturnForm />}
          {current?.key === 'historial' && <ReturnHistory />}
        </>
      )}
    </PageBody>
  );
};

/** Dato con etiqueta en gris. */
const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
      {value || '—'}
    </Typography>
  </Box>
);

interface LoanItem {
  id: number;
  quantity: number;
  returnedQuantity: number;
  unitPrice: number;
  isReplacement: boolean;
  marginPercent?: number | string | null;
  product: { id: number; title: string };
}

type ReturnProduct = { id: number; title: string; delivered: number; returned: number; price: number; utility: number | null };

const ReturnForm: React.FC = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const { canCreate } = useResourcePermissions(INVENTORY_ROUTES.returns);
  const [loanId, setLoanId] = useState('');
  const [loan, setLoan] = useState<any | null>(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productId, setProductId] = useState<number | ''>('');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('DEFECTO_FABRICA');
  const [condition, setCondition] = useState('DANADO');
  const [resolution, setResolution] = useState('CAMBIO');
  const [observation, setObservation] = useState('');
  const [date, setDate] = useState(nowOperationalDateString());
  const [errors, setErrors] = useState<{ product?: string; quantity?: string }>({});

  /** Solo préstamos de rutas asignadas al usuario (superadmin: todos), igual que el resto del módulo. */
  const loanInUserScope = async (record: any) => {
    const officeId = Number(record?.route?.office?.id);
    const routeId = Number(record?.route?.id);
    if (!officeId || !routeId) return false;
    try {
      const { data } = await axios.get(`offices/${officeId}/routes`);
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      return list.some((c: any) => Number(c.id) === routeId);
    } catch {
      return false;
    }
  };

  const findLoan = async () => {
    if (!loanId.trim()) return;
    setSearching(true);
    try {
      const { data } = await axios.get(`loans/${loanId.trim()}`);
      if (!data?.id) {
        showAlert(t('writeOffs.returns.notFound'), 'warning');
        setLoan(null);
      } else if (!(await loanInUserScope(data))) {
        showAlert(t('writeOffs.returns.noAccess'), 'warning');
        setLoan(null);
      } else {
        setLoan(data);
        setProductId('');
      }
    } catch (error) {
      showAlert(apiErrorMessage(error, t('writeOffs.returns.loanError')), 'error');
      setLoan(null);
    } finally {
      setSearching(false);
    }
  };

  // Entregado / devuelto por producto (incluye reposiciones).
  const products = useMemo(() => {
    const map = new Map<number, ReturnProduct>();
    ((loan?.items as LoanItem[]) || []).forEach((i) => {
      const itemUtility = i.marginPercent;
      const cur = map.get(i.product.id) || {
        id: i.product.id,
        title: i.product.title,
        delivered: 0,
        returned: 0,
        price: Number(i.unitPrice || 0),
        // % con el que se prestó (cálculo por producto); null = utilidad de la constante.
        utility: itemUtility === null || itemUtility === undefined ? null : Number(itemUtility),
      };
      cur.delivered += Number(i.quantity || 0);
      cur.returned += Number(i.returnedQuantity || 0);
      map.set(i.product.id, cur);
    });
    return Array.from(map.values());
  }, [loan]);
  const selected = products.find((p) => p.id === productId);
  const returnable = selected ? selected.delivered - selected.returned : 0;
  const utility = selected && selected.utility !== null ? Number(selected.utility) : Number(loan?.interestRate || 0);
  const utilitySource = selected && selected.utility !== null ? t('writeOffs.returns.sourceProduct') : t('writeOffs.returns.sourceLoanType');
  const estimatedCredit = selected ? Math.round(selected.price * (Number(quantity) || 0) * (1 + utility / 100)) : 0;

  const save = async () => {
    if (!canCreate || !loan) return;
    const next: { product?: string; quantity?: string } = {};
    if (productId === '') next.product = t('writeOffs.errProduct');
    if (!(Number(quantity) > 0)) next.quantity = t('writeOffs.errQuantity');
    setErrors(next);
    if (next.product || next.quantity) return;
    setSaving(true);
    try {
      await axios.post('product-returns', {
        loanId: loan.id,
        productId: productId,
        quantity: Number(quantity),
        reason,
        condition,
        resolution,
        date,
        observation: observation || null,
        clientRequestId: newRequestId('return'),
      });
      showAlert(t('writeOffs.returns.saved'), 'success');
      setQuantity('1');
      setObservation('');
      await findLoan();
    } catch (error) {
      showAlert(apiErrorMessage(error, t('writeOffs.returns.saveError')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const productColumns: DataTableColumn<ReturnProduct>[] = [
    { id: 'title', header: t('writeOffs.product'), value: (p) => p.title },
    { id: 'delivered', header: t('writeOffs.returns.delivered'), align: 'right', value: (p) => p.delivered },
    { id: 'returned', header: t('writeOffs.returns.returned'), align: 'right', value: (p) => p.returned },
    { id: 'price', header: t('writeOffs.returns.price'), align: 'right', value: (p) => p.price, render: (p) => formatMoney(p.price) },
  ];

  const select = (label: string, value: string, onChange: (v: string) => void, options: { value: string; label: string }[], tour?: string) => (
    <TextField select fullWidth label={label} value={value} onChange={(e) => onChange(String(e.target.value))} disabled={!canCreate} {...(tour ? { 'data-tour': tour } : {})}>
      {options.map((r) => (
        <MenuItem key={r.value} value={r.value}>
          {r.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <>
      <Section title={t('writeOffs.returns.findTitle')} description={t('writeOffs.returns.findHelp')} tour="returns-find">
        <FilterGrid sx={{ maxWidth: 560 }}>
          <TextField
            fullWidth
            label={t('writeOffs.returns.loanNumber')}
            value={loanId}
            onChange={(e) => setLoanId(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && findLoan()}
            disabled={!canCreate}
            inputProps={{ inputMode: 'numeric' }}
          />
          <LoadingButton variant="outlined" startIcon={<SearchOutlinedIcon />} onClick={findLoan} loading={searching} disabled={!canCreate || !loanId} sx={{ minHeight: 56 }}>
            {t('common.search')}
          </LoadingButton>
        </FilterGrid>
      </Section>
      {loan && (
        <Section title={t('writeOffs.returns.loanTitle', { id: loan.id })} tour="returns-loan">
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))' }}>
              <Fact label={t('writeOffs.returns.client')} value={loan.client?.fullName} />
              <Fact label={t('common.route')} value={loan.route?.name} />
              <Fact label={t('writeOffs.returns.balance')} value={formatMoney(loan.balance)} />
              <Fact label={t('writeOffs.returns.total')} value={formatMoney(loan.totalValue)} />
            </Box>
            {products.length === 0 ? (
              <Notice severity="info">{t('writeOffs.returns.noProducts')}</Notice>
            ) : (
              <DataTable<ReturnProduct> rows={products} columns={productColumns} getRowId={(p) => p.id} searchable={false} maxHeight="none" ariaLabel={t('writeOffs.returns.loanProducts')} />
            )}
          </Box>
        </Section>
      )}
      {loan && products.length > 0 && (
        <Section title={t('writeOffs.returns.tabRegister')} tour="returns-register">
          <Box sx={{ display: 'grid', gap: 2, maxWidth: 1100 }}>
            <FieldGrid md={3}>
              <TextField
                select
                fullWidth
                required
                label={t('writeOffs.returns.returnedProduct')}
                data-tour="returns-product"
                value={productId === '' ? '' : String(productId)}
                onChange={(e) => {
                  setProductId(Number(e.target.value) || '');
                  setErrors((x) => ({ ...x, product: undefined }));
                }}
                disabled={!canCreate}
                error={Boolean(errors.product)}
                helperText={errors.product}
                sx={{ gridColumn: { md: 'span 2' } }}
              >
                {products
                  .filter((p) => p.delivered > p.returned)
                  .map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.title} · {t('writeOffs.returns.canReturn', { n: p.delivered - p.returned })}
                    </MenuItem>
                  ))}
              </TextField>
              <IntField
                fullWidth
                required
                label={t('writeOffs.quantity')}
                value={quantity}
                onChange={(v) => {
                  setQuantity(v);
                  setErrors((x) => ({ ...x, quantity: undefined }));
                }}
                disabled={!canCreate}
                error={Boolean(errors.quantity) || (selected ? Number(quantity) > returnable : false)}
                helperText={errors.quantity ?? (selected ? t('writeOffs.max', { n: returnable }) : undefined)}
              />
              {select(t('writeOffs.reason'), reason, setReason, REASONS(t))}
              {select(t('writeOffs.returns.productCondition'), condition, setCondition, CONDITIONS(t), 'returns-condition')}
              {select(t('writeOffs.returns.resolutionLabel'), resolution, setResolution, RESOLUTIONS(t), 'returns-resolution')}
              <DateField fullWidth label={t('common.date')} value={date || null} onChange={(v) => setDate(v ?? '')} disabled={!canCreate} />
              {selected && resolution === 'DESCUENTO' && (
                <Box sx={fullRow}>
                  <Notice severity="warning">{t('writeOffs.returns.discountWarning', { amount: formatMoney(estimatedCredit), utility, source: utilitySource })}</Notice>
                </Box>
              )}
              {selected && resolution === 'CAMBIO' && (
                <Box sx={fullRow}>
                  <Notice severity="info">{t('writeOffs.returns.exchangeInfo')}</Notice>
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={t('writeOffs.observation')}
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                disabled={!canCreate}
                sx={fullRow}
              />
            </FieldGrid>
            <FormFooter tour="returns-save">
              <SaveButton allowed={canCreate} deniedTooltip={t('writeOffs.returns.noPermission')} loading={saving} onClick={save} startIcon={<AssignmentReturnOutlinedIcon />}>
                {t('writeOffs.returns.tabRegister')}
              </SaveButton>
            </FormFooter>
          </Box>
        </Section>
      )}
    </>
  );
};

type ReturnRow = {
  id: number;
  date: string;
  loan?: { id: number; client?: { fullName?: string } | null } | null;
  route?: { name?: string } | null;
  product?: { title?: string } | null;
  quantity: number;
  reason: string;
  itemCondition: string;
  resolution: string;
  creditTotal?: number | string | null;
};

const ReturnHistory: React.FC = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [filter, setFilter] = useState<RangeFilter>({ startDate: `${nowOperationalDateString().slice(0, 8)}01`, endDate: nowOperationalDateString(), officeId: '' });
  const [rows, setRows] = useState<ReturnRow[]>([]);
  const [loading, setLoading] = useState(true);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('product-returns', { params: filterParams(filter) });
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('writeOffs.returns.historyError')), 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showAlert, t]);

  useEffect(() => {
    fetchOffices().then(setOffices).catch(() => setOffices([]));
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: DataTableColumn<ReturnRow>[] = [
    { id: 'date', header: t('common.date'), value: (r) => formatDate(r.date), sortable: true },
    { id: 'loan', header: t('writeOffs.returns.loan'), value: (r) => r.loan?.id ?? '', sortable: true },
    { id: 'client', header: t('writeOffs.returns.client'), value: (r) => r.loan?.client?.fullName ?? '', sortable: true },
    { id: 'route', header: t('common.route'), value: (r) => r.route?.name ?? '', hideBelow: 'md' },
    { id: 'product', header: t('writeOffs.product'), value: (r) => r.product?.title ?? '', sortable: true },
    { id: 'quantity', header: t('writeOffs.returns.quantityShort'), align: 'right', value: (r) => Number(r.quantity) },
    { id: 'reason', header: t('writeOffs.reason'), value: (r) => labelOf(REASONS(t), r.reason), hideBelow: 'md' },
    { id: 'condition', header: t('writeOffs.returns.condition'), value: (r) => labelOf(CONDITIONS(t), r.itemCondition).split(' (')[0], hideBelow: 'md' },
    { id: 'resolution', header: t('writeOffs.returns.resolutionLabel'), value: (r) => labelOf(RESOLUTIONS(t), r.resolution).split(' (')[0] },
    {
      id: 'credit',
      header: t('writeOffs.returns.discount'),
      align: 'right',
      value: (r) => (r.resolution === 'DESCUENTO' ? Number(r.creditTotal ?? 0) : null),
      render: (r) => (r.resolution === 'DESCUENTO' ? formatMoney(r.creditTotal) : '—'),
    },
  ];

  return (
    <>
      <Card>
        <CardContent>
          <DateRangeOfficeFilter offices={offices} value={filter} onChange={setFilter} onSearch={search} loading={loading} tourPrefix="returns-history" />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <DataTable<ReturnRow>
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            ariaLabel={t('writeOffs.tabHistory')}
            tourId="returns-history"
            exportFileName={t('writeOffs.returns.exportFile')}
            initialSort={{ columnId: 'date', direction: 'desc' }}
            emptyState={<EmptyState icon={<AssignmentReturnOutlinedIcon fontSize="inherit" />} title={t('writeOffs.returns.empty')} description={t('writeOffs.returns.emptyHint')} />}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default ProductReturns;
